import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Key, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usersAPI } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { User } from "../types";

interface ResetUserPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const MIN_PASSWORD_LENGTH = 8;

export function ResetUserPasswordModal({
  isOpen,
  onClose,
  user,
}: ResetUserPasswordModalProps) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("No user selected");
      return usersAPI.adminSetPassword(user._id, password, confirm);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS });
      toast.success(
        `Password updated for ${user?.first_name || "user"}`,
      );
      resetForm();
      onClose();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to update password");
    },
  });

  const resetForm = () => {
    setPassword("");
    setConfirm("");
  };

  const handleClose = () => {
    if (!resetPasswordMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit =
    password.length >= MIN_PASSWORD_LENGTH &&
    password === confirm &&
    !resetPasswordMutation.isPending;

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Reset User Password
          </DialogTitle>
          <DialogDescription>
            Set a new password for{" "}
            <span className="font-medium">
              {user.first_name} {user.last_name}
            </span>{" "}
            ({user.email}). The user will be able to log in with this password
            immediately. Any existing sessions will be invalidated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              disabled={resetPasswordMutation.isPending}
            />
            {tooShort && (
              <p className="text-sm text-destructive">
                Password must be at least {MIN_PASSWORD_LENGTH} characters.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="Re-enter new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={resetPasswordMutation.isPending}
            />
            {mismatch && (
              <p className="text-sm text-destructive">
                Passwords do not match.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={resetPasswordMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => resetPasswordMutation.mutate()}
            disabled={!canSubmit}
          >
            {resetPasswordMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Key className="mr-2 h-4 w-4" />
                Update Password
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
